import type { AgentControllerInterfaceSnapshot } from "@wakeloop/schemas/types";

// Background report syncs reuse the last successfully probed snapshot for this
// long before paying for another controller probe. Probes cold-start real
// controller processes (codex app-server, opencode serve), so the background
// cadence must stay far cheaper than the 5-minute report sync interval.
const LAST_GOOD_REUSE_TTL_MS = 15 * 60_000;

// A probe that could not complete (spawn failure, timeout, early exit) is
// load evidence, not controller truth. Background reporting keeps the last
// completed snapshot until this many probes in a row fail to complete.
const TRANSIENT_FAILURE_REPORT_THRESHOLD = 3;

// A controller's first probe has no last-good snapshot to fall back on, so a
// timeout/spawn-failure freezes it at Error until the user or the next
// background cadence checks again. A single delayed re-probe covers the common
// case where the just-finished setup left the machine too busy for the cold
// controller start to complete in time. This delay gives that machine room to
// settle before the re-probe cold-starts the controller again.
export const AGENT_CONTROLLER_INTERFACE_REPROBE_DELAY_MS = 45_000;

// One-shot re-probe lifecycle for a controller that has never produced a
// completed snapshot. It only decides whether the report-sync scheduler brings
// the next pass forward; it never becomes reported controller status, so
// fail-closed Error reporting is never smoothed by an optimistic state.
//   idle      -> no re-probe pending (also the state after any success)
//   armed     -> first probe failed to complete; request one delayed re-probe
//   attempted -> the single re-probe also failed; no further automatic re-probe
type AgentControllerInterfaceReprobeState = "armed" | "attempted" | "idle";

type AgentControllerInterfaceKeeperEntry = {
  failStreak: number;
  lastGoodSnapshot: AgentControllerInterfaceSnapshot | null;
  lastSuccessAtMs: number;
  reprobe: AgentControllerInterfaceReprobeState;
};

const keeperEntries = new Map<string, AgentControllerInterfaceKeeperEntry>();

export function buildAgentControllerInterfaceKeeperKey(input: {
  agentId: string;
  commandPath: string | null;
}): string {
  return `${input.agentId}\u0000${input.commandPath ?? ""}`;
}

export function readReusableAgentControllerInterfaceSnapshot(input: {
  keeperKey: string;
  nowMs: number;
}): AgentControllerInterfaceSnapshot | null {
  const entry = keeperEntries.get(input.keeperKey);
  if (!entry?.lastGoodSnapshot) {
    return null;
  }
  if (input.nowMs - entry.lastSuccessAtMs >= LAST_GOOD_REUSE_TTL_MS) {
    return null;
  }
  return entry.lastGoodSnapshot;
}

export function recordAgentControllerInterfaceProbeSuccess(input: {
  keeperKey: string;
  nowMs: number;
  snapshot: AgentControllerInterfaceSnapshot;
}): void {
  // A completed probe clears the re-probe lifecycle: the controller now has a
  // last-good snapshot, so it is no longer in the never-succeeded state.
  keeperEntries.set(input.keeperKey, {
    failStreak: 0,
    lastGoodSnapshot: input.snapshot,
    lastSuccessAtMs: input.nowMs,
    reprobe: "idle",
  });
}

export function recordAgentControllerInterfaceProbeFailure(input: {
  keeperKey: string;
}): { heldSnapshot: AgentControllerInterfaceSnapshot | null } {
  const entry = keeperEntries.get(input.keeperKey) ?? {
    failStreak: 0,
    lastGoodSnapshot: null,
    lastSuccessAtMs: 0,
    reprobe: "idle" as AgentControllerInterfaceReprobeState,
  };
  entry.failStreak += 1;
  // Only the never-succeeded (no fallback) case arms a re-probe, and only from
  // idle. A failure that still has a last-good snapshot is transient-load
  // evidence handled by the hold logic below; it needs no special re-probe.
  // Retiring the arm is owned by consumeArmedAgentControllerInterfaceReprobe:
  // an entry already past its one shot (attempted) never re-arms here.
  if (entry.lastGoodSnapshot === null && entry.reprobe === "idle") {
    entry.reprobe = "armed";
  }
  keeperEntries.set(input.keeperKey, entry);
  if (
    entry.lastGoodSnapshot &&
    entry.failStreak < TRANSIENT_FAILURE_REPORT_THRESHOLD
  ) {
    return { heldSnapshot: entry.lastGoodSnapshot };
  }
  return { heldSnapshot: null };
}

// Reports whether any controller's first probe armed a re-probe and RETIRES
// those arms (armed -> attempted) in the same call, so the report-sync
// scheduler brings the next pass forward at most once per arm. Retiring at
// observation — rather than waiting for a later probe of that same controller
// to clear it — is what keeps a stuck arm from pinning the sync cadence
// forever: a controller can leave the ready state after arming (a controller
// CLI upgrade/repair nulls its launch contract), after which it is never probed
// again and nothing else would ever clear the arm.
export function consumeArmedAgentControllerInterfaceReprobe(): boolean {
  let armed = false;
  for (const entry of keeperEntries.values()) {
    if (entry.reprobe === "armed") {
      entry.reprobe = "attempted";
      armed = true;
    }
  }
  return armed;
}

// Pure scheduler decision for the next runtime agent controller report sync.
// When a first-probe re-probe is armed, the next pass is brought forward to at
// most the re-probe delay; otherwise it keeps the caller's base cadence. The
// re-probe pass itself runs a real background probe (a still-Error controller
// has no reusable snapshot) and flows through the existing report/refresh path,
// while an already-healthy controller reuses its held snapshot for free.
export function resolveNextRuntimeAgentControllerReportSyncAtMs(input: {
  nowMs: number;
  baseRetryMs: number;
  hasArmedReprobe: boolean;
}): number {
  const baseNextMs = input.nowMs + input.baseRetryMs;
  if (!input.hasArmedReprobe) {
    return baseNextMs;
  }
  return Math.min(
    baseNextMs,
    input.nowMs + AGENT_CONTROLLER_INTERFACE_REPROBE_DELAY_MS
  );
}

export function resetAgentControllerInterfaceKeeperForTests(): void {
  keeperEntries.clear();
}

export function ageAgentControllerInterfaceKeeperForTests(byMs: number): void {
  for (const entry of keeperEntries.values()) {
    entry.lastSuccessAtMs -= byMs;
  }
}
